import { Injectable } from "@angular/core";
import { DataService } from "./data.service";
import { catchError, map, Observable, of, tap, throwError } from "rxjs";
import { environment } from "src/environments/environment";
import { SearchRecentAppointments } from "../models/search-recent-appointments.model";
import { resolveScheduleVisitTypeSlug } from "../models/schedule-visit-types";
import { ApplicationCacheService } from "./application-cache.service";
import { EncounterApis, ApiStatus } from "./utils";
import { cloneDeep } from "lodash";

export interface SearchListingOptions {
  /** When set, passed to the API and used to filter results client-side. */
  visitType?: string;
}

@Injectable({
  providedIn: "root",
})
export class SearchAppointmentService {
  constructor(
    private readonly _dataService: DataService,
    private readonly _applicationCacheService: ApplicationCacheService,
  ) {}

  get isRecentAppointmentsAlreadyLoaded(): boolean {
    return (
      this._applicationCacheService.getApiStatus(EncounterApis.RecentAppointments) ===
      ApiStatus.Success
    );
  }

  get recentAppointmentsCache() {
    return cloneDeep(this._applicationCacheService.applicationCache.recentAppointments);
  }

  getRecentAppointments(): Observable<SearchRecentAppointments[]> {
    if (this.isRecentAppointmentsAlreadyLoaded) {
      return of(this.recentAppointmentsCache);
    }

    const url = `${this.apiBaseUrl}api/v1/search`;
    return this._dataService.get(url).pipe(
      tap((response: any) => {
        this._applicationCacheService.setApiStatus(
          ApiStatus.Success,
          EncounterApis.RecentAppointments
        );
      }),
      catchError((error) => {
          this._applicationCacheService.setApiStatus(
            ApiStatus.Failed,
            EncounterApis.HpiSummary
          );
        return throwError(() => error);
      })
    );
  }

  getSearchListing(
    term: string,
    options?: SearchListingOptions
  ): Observable<SearchRecentAppointments[]> {
    const params = new URLSearchParams({
      term: term ?? "",
    });
    const visitType = (options?.visitType ?? "").trim();
    if (visitType) {
      params.set("visitType", visitType);
    }

    const url = `${this.apiBaseUrl}api/v1/search/term?${params.toString()}`;

    return this._dataService.get(url).pipe(
      map((results) =>
        this.filterSearchListingByVisitType(
          (results ?? []) as SearchRecentAppointments[],
          visitType
        )
      ),
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  private filterSearchListingByVisitType(
    results: SearchRecentAppointments[],
    visitType: string
  ): SearchRecentAppointments[] {
    const targetSlug = resolveScheduleVisitTypeSlug(visitType);
    if (!targetSlug) {
      return results;
    }

    return results.filter(
      (row) => resolveScheduleVisitTypeSlug(row.visitType ?? "") === targetSlug
    );
  }

  saveRecentAppointment(
    encounterId: string
  ): Observable<SearchRecentAppointments[]> {
    const url = this.apiBaseUrl + "api/v1/search";
    return this._dataService.post(url, { encounterId });
  }

  get apiBaseUrl(): string {
    return environment.apiBaseUrl;
  }
}
